import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/routing";
import { Button } from "./ui/button";
import { ChevronDown, Languages } from "lucide-react";

type ToggleLanguageProps = {
  variant?: "desktop" | "mobile";
  className?: string;
};

const ToggleLanguage = ({ variant = "desktop", className }: ToggleLanguageProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("general");

  const handleLanguageChange = (newLocale: string) => {
    router.push(pathname, { locale: newLocale });
    setIsOpen(false);
  };

  // Mobile variant - simpler button group style
  if (variant === "mobile") {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <div className="flex items-center gap-2 px-2 text-xs uppercase tracking-widest text-white/60 mb-1">
          <Languages size={14} className="text-white/60" />
          <span>{t("language") || "Language"}</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant={locale === "en" ? "default" : "outline"}
            className={cn(
              "flex-1 rounded-lg border transition-all",
              locale === "en"
                ? "bg-gradient-to-r from-[#8100FF] to-[#323DFB] text-white border-transparent"
                : "bg-transparent border-white/20 text-white hover:bg-white/5"
            )}
            onClick={() => handleLanguageChange("en")}
          >
            EN
          </Button>
          <Button
            variant={locale === "ar" ? "default" : "outline"}
            className={cn(
              "flex-1 rounded-lg border transition-all",
              locale === "ar"
                ? "bg-gradient-to-r from-[#8100FF] to-[#323DFB] text-white border-transparent"
                : "bg-transparent border-white/20 text-white hover:bg-white/5"
            )}
            onClick={() => handleLanguageChange("ar")}
          >
            العربية
          </Button>
        </div>
      </div>
    );
  }

  // Desktop variant - original dropdown design
  return (
    <div className={cn("relative w-fit", className)}>
      {/* Main button - changes shape when open */}
      <div
        className={cn(
          "border border-[#636363] bg-black-bg overflow-visible relative transition-all",
          isOpen ? "rounded-t-full" : "rounded-full",
          isOpen && "border-b-0"
        )}
      >
        <Button
          variant="ghost"
          className="justify-between py-2 px-3 gap-2 h-fit hover:bg-transparent w-full"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-white text-sm font-medium hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text">
            {locale === "en" ? "EN" : "العربية"}
          </span>
          <ChevronDown
            className={cn(
              "h-5 w-5 shrink-0 transition-transform duration-200 text-white",
              isOpen && "rotate-180"
            )}
          />
        </Button>
      </div>

      {/* Dropdown container - seamlessly extends the button */}
      {isOpen && (
        <>
          {/* Overlay to close on outside click */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown container with the other language option */}
          <div
            className={cn(
              "absolute top-full bg-black-bg border border-[#636363] border-t-0 rounded-b-full shadow-lg z-50 min-w-full",
              locale === "en" ? "left-0" : "right-0"
            )}
          >
            <Button
              className="bg-transparent justify-start py-2 px-3 hover:bg-white/5 w-full h-fit hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition-all text-white text-sm font-medium rounded-b-full"
              onClick={() => handleLanguageChange(locale === "en" ? "ar" : "en")}
            >
              {locale === "en" ? "العربية" : "EN"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ToggleLanguage;
